import { useCallback, useState } from 'react'

/**
 * Hook de estado que persiste em localStorage entre sessões.
 *
 * Trata JSON inválido e falhas de storage silenciosamente (reverte para default).
 *
 * @example
 * const [sidebarOpen, setSidebarOpen] = usePersistedState('sidebar-open', true)
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key)
      if (stored === null) return defaultValue
      return JSON.parse(stored) as T
    } catch {
      return defaultValue
    }
  })

  const set = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState(prev => {
        const next =
          typeof value === 'function' ? (value as (p: T) => T)(prev) : value
        try {
          localStorage.setItem(key, JSON.stringify(next))
        } catch {
          // ignore storage errors
        }
        return next
      })
    },
    [key]
  )

  return [state, set]
}
